import React, { useState, useEffect } from 'react';
import { useTheme } from '../../context/ThemeContext';

const SliceTransition = ({ 
  isActive, 
  onComplete, 
  targetComponent, 
  duration = 3000 
}) => {
  const [fadeOpacity, setFadeOpacity] = useState(0);
  const [barPosition, setBarPosition] = useState(-20);
  const [barVisible, setBarVisible] = useState(false);
  const [barTransition, setBarTransition] = useState(true);
  const [isFirstBar, setIsFirstBar] = useState(true);
  const { theme } = useTheme();

  useEffect(() => {
    if (!isActive) {
      setFadeOpacity(0);
      setBarPosition(-20);
      setBarVisible(false);
      setBarTransition(true);
      setIsFirstBar(true);
      return;
    }

    // Step 1: Fade screen
    setFadeOpacity(1);
    
    // Step 2: First bar animation (immediate)
    setBarVisible(true);
    setTimeout(() => setBarPosition(120), 10);
    
    // Step 3: Second bar as wipe/curtain effect (reduced gap only)
    const timer2 = setTimeout(() => {
      setIsFirstBar(false); // Switch to wipe mode
      setBarTransition(false); // Disable transition for instant reset
      setBarPosition(-20); // Reset to top instantly
      setTimeout(() => {
        setBarTransition(true); // Re-enable transition
        setBarPosition(120); // Move down as curtain wipe
      }, 10);
    }, 750); // Half the gap between bars
    
    // Step 4: Hide the wipe bar and show content (much faster)
    const timer3 = setTimeout(() => {
      setBarVisible(false); // Hide the wipe bar
      if (onComplete) onComplete(); // Trigger content reveal
    }, 1000); // Adjusted for faster second bar timing

    return () => {
      clearTimeout(timer2);
      clearTimeout(timer3);
    };
  }, [isActive, onComplete]);

  if (!isActive && fadeOpacity === 0) return null;

  return (
    <div className="fixed inset-0 pointer-events-auto overflow-hidden slice-transition-overlay">
      {/* Theme-aware fade overlay */}
      <div 
        className="absolute inset-0 transition-opacity duration-2000 ease-in-out"
        style={{
          opacity: fadeOpacity,
          backgroundColor: theme === 'dark' ? '#000000' : '#ffffff',
        }}
      />
      
      {/* Bar - First flash, then curtain wipe */}
      {barVisible && (
        <div 
          className="absolute left-0 w-full ease-in-out"
          style={{
            backgroundColor: theme === 'dark' ? '#ffffff' : '#000000',
            transform: `translateY(${barPosition}vh)`,
            opacity: 1,
            filter: 'blur(0.5px)',
            transition: barTransition ? 'all 0.25s ease-in-out' : 'none',
            height: isFirstBar ? '10vh' : '100vh', // Second bar covers full height for wipe
            boxShadow: theme === 'dark' 
              ? `0 0 60px 30px rgba(255, 255, 255, 0.8),
                 0 0 100px 60px rgba(255, 255, 255, 0.5),
                 0 0 140px 90px rgba(255, 255, 255, 0.3),
                 0 20px 80px 10px rgba(255, 255, 255, 0.4),
                 inset 0 0 20px rgba(255, 255, 255, 0.6)`
              : `0 0 60px 30px rgba(0, 0, 0, 0.8),
                 0 0 100px 60px rgba(0, 0, 0, 0.5),
                 0 0 140px 90px rgba(0, 0, 0, 0.3),
                 0 20px 80px 10px rgba(0, 0, 0, 0.4),
                 inset 0 0 20px rgba(0, 0, 0, 0.6)`,
          }}
        />
      )}
      
      {/* New content reveals progressively as bar moves down */}
      {!isFirstBar && targetComponent && (
        <div 
          className="absolute inset-0"
          style={{
            clipPath: `polygon(0 0, 100% 0, 100% ${Math.max(0, barPosition - 20)}vh, 0 ${Math.max(0, barPosition - 20)}vh)`,
            opacity: Math.min(1, Math.max(0, (barPosition + 20) / 100)), // Fade in as bar moves
            transition: 'opacity 0.2s ease-out',
          }}
        >
          {targetComponent}
        </div>
      )}
    </div>
  );
};

export default SliceTransition;